import React, { useEffect, useMemo } from 'react';
import { Col, Form, Input, Radio, Row } from 'antd';
import styled from 'styled-components';
import Text from 'components/Text';
import Box from 'components/Box';
import { ESalaryPayrollType } from 'features/settingStaff/services/constants';
import { createPerTypeHandlers } from './utils';
import CurrencyInputPrecision from 'components/CurrencyInputPrecision';
import shopSelectors from 'services/shop/selectors';

const SalaryPerTypes: React.FC = () => {
  const form = Form.useFormInstance();
  const payrollType =
    Form.useWatch('payrollType', form) || ESalaryPayrollType.DEFAULT;

  const { handlePerTypeCashChange, handlePerTypeCheckChange } = createPerTypeHandlers(form);

  const isW2 = useMemo(
    () => payrollType === ESalaryPayrollType.W2,
    [payrollType]
  );
  const is1099 = !isW2;

  const allSetting = shopSelectors.data.allSetting();

  return (
    <SalaryPerTypesStyled className='SalaryPerTypesStyled'>
      <Row style={{ marginBottom: '16px' }}>
        <Col span={24}>
          <Form.Item
            name='payrollType'
            initialValue={ESalaryPayrollType.DEFAULT}
          >
            <Radio.Group>
              <Radio value={ESalaryPayrollType.DEFAULT}>1099</Radio>
              <Radio value={ESalaryPayrollType.W2}>W-2</Radio>
            </Radio.Group>
          </Form.Item>
        </Col>
      </Row>

      {is1099 && (
        <>
          <Row gutter={[16, 16]}>
            <Col flex={1}>
              <Form.Item
                required={true}
                name='perTypeCash'
                label={
                  <Box display='flex' gap='1'>
                    % {allSetting?.labelSalaryCashOrBonus} <Text color='error_6'>(*)</Text>
                  </Box>
                }
                rules={[
                  {
                    required: is1099,
                    message: 'Missing cash',
                  },
                  () => ({
                    validator(_, value) {
                      if (!value && value !== 0) {
                        return Promise.reject(new Error('Cash is required'));
                      }

                      if (isNaN(Number(value))) {
                        return Promise.reject(new Error('Cash must be a number'));
                      }

                      if (Number(value) < 0 || Number(value) > 100) {
                        return Promise.reject(
                          new Error('Cash should be between 0 and 100')
                        );
                      }

                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <Input type='number' onChange={handlePerTypeCashChange} />
              </Form.Item>
            </Col>
            <Col flex={1}>
              <Form.Item
                required={true}
                name='perTypeCheck'
                label={
                  <Box display='flex' gap='1'>
                    % Check<Text color='error_6'>(*)</Text>
                  </Box>
                }
                rules={[
                  {
                    required: is1099,
                    message: 'Missing check',
                  },
                  () => ({
                    validator(_, value) {
                      if (!value && value !== 0) {
                        return Promise.reject(new Error('Check is required'));
                      }

                      if (isNaN(Number(value))) {
                        return Promise.reject(
                          new Error('Check must be a number')
                        );
                      }

                      if (Number(value) < 0 || Number(value) > 100) {
                        return Promise.reject(
                          new Error('Check should be between 0 and 100')
                        );
                      }

                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <Input type='number' onChange={handlePerTypeCheckChange} />
              </Form.Item>
            </Col>
            <Col flex={1}>
              <Form.Item
                name='special_fee'
                label='Special Fee'
                rules={[
                  () => ({
                    validator(_, value) {
                      if (value && isNaN(Number(value))) {
                        return Promise.reject(
                          new Error('Supply Share must be a number')
                        );
                      }

                      if (value && Number(value) < 0) {
                        return Promise.reject(
                          new Error('Supply Share cannot be negative')
                        );
                      }

                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <CurrencyInputPrecision height={'3rem'} />
              </Form.Item>
            </Col>
          </Row>
          <Box
              flexDirection='column'
              bgColor='fill_1'
              bStyle='solid'
              borderRadius='4px'
              px='4'
              pb='2'
              b='line_1'
            >
              <Row align='middle'>
                <Text variant='H7' mr={0.25} py={1} color='v2_primary_bg_6'>
                  Supply Share
                </Text>
                <Text color='text_2' variant='BODY_1'>
                  (Salon receives $1 for every $100 the staff earns)
                </Text>
              </Row>
              <Row gutter={[16, 16]}>
                <Col xs={24} md={12}>
                  <Form.Item
                    name='supplySale'
                    label={
                      <Box>
                        <Box display='flex' gap='1'>
                          Sale ($)
                        </Box>
                      </Box>
                    }
                    rules={[
                      () => ({
                        validator(_, value) {
                          if (isNaN(Number(value))) {
                            return Promise.reject(
                              new Error('Sale amount must be a number')
                            );
                          }

                          if (Number(value) < 0) {
                            return Promise.reject(
                              new Error('Sale amount must be greater or equal to 0')
                            );
                          }

                          return Promise.resolve();
                        },
                      }),
                    ]}
                  >
                    <CurrencyInputPrecision height={'3rem'} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    name='supplyFee'
                    label={
                      <Box display='flex' gap='1'>
                        Fee ($)
                      </Box>
                    }
                    rules={[
                      () => ({
                        validator(_, value) {
                          if (isNaN(Number(value))) {
                            return Promise.reject(
                              new Error('Fee amount must be a number')
                            );
                          }

                          if (Number(value) < 0) {
                            return Promise.reject(
                              new Error('Fee amount must be greater or equal to 0')
                            );
                          }

                          return Promise.resolve();
                        },
                      }),
                    ]}
                  >
                    <CurrencyInputPrecision height={'3rem'} />
                  </Form.Item>
                </Col>
              </Row>
            </Box>
          </>
      )}

      {isW2 && (
        <>
          <Row gutter={[16, 16]}>
            <Col flex={1}>
              <Form.Item
                required={true}
                name='perTypeCash'
                label={
                  <Box display='flex' gap='1'>
                    % Cash <Text color='error_6'>(*)</Text>
                  </Box>
                }
                rules={[
                  {
                    required: isW2,
                    message: 'Missing cash',
                  },
                  () => ({
                    validator(_, value) {
                      if (!value && value !== 0) {
                        return Promise.reject(new Error('Cash is required'));
                      }

                      if (isNaN(Number(value))) {
                        return Promise.reject(new Error('Cash must be a number'));
                      }

                      if (Number(value) < 0 || Number(value) > 100) {
                        return Promise.reject(
                          new Error('Cash should be between 0 and 100')
                        );
                      }

                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <Input type='number' onChange={handlePerTypeCashChange} />
              </Form.Item>
            </Col>
            <Col flex={1}>
              <Form.Item
                required={true}
                name='perTypeCheck'
                label={
                  <Box display='flex' gap='1'>
                    % Check<Text color='error_6'>(*)</Text>
                  </Box>
                }
                rules={[
                  {
                    required: isW2,
                    message: 'Missing check',
                  },
                  () => ({
                    validator(_, value) {
                      if (!value && value !== 0) {
                        return Promise.reject(new Error('Check is required'));
                      }

                      if (isNaN(Number(value))) {
                        return Promise.reject(
                          new Error('Check must be a number')
                        );
                      }

                      if (Number(value) < 0 || Number(value) > 100) {
                        return Promise.reject(
                          new Error('Check should be between 0 and 100')
                        );
                      }

                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <Input type='number' onChange={handlePerTypeCheckChange} />
              </Form.Item>
            </Col>
            <Col flex={1}>
              <Form.Item
                name='federalTax'
                label={
                  <Box display='flex' gap='1'>
                    <Text color='v2_green_7' style={{'fontSize': '18px'}}>Federal Tax Withheld (%)</Text>
                  </Box>
                }
                rules={[
                  {
                    required: isW2,
                    message: 'Federal tax is required',
                  },
                  () => ({
                    validator(_, value) {
                      if (isNaN(Number(value))) {
                        return Promise.reject(
                          new Error('Federal tax must be a number')
                        );
                      }

                      if (Number(value) < 0 || Number(value) > 100) {
                        return Promise.reject(
                          new Error('Federal tax should be between 0 and 100')
                        );
                      }

                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <Input type='number' placeholder='0' />
              </Form.Item>
            </Col>
            <Col flex={1}>
              <Form.Item
                name='socialSecurityTax'
                label={
                  <Box display='flex' gap='1'>
                    <Text color='v2_green_7' style={{'fontSize': '18px'}}>Social Security Tax Withheld (%)</Text>
                  </Box>
                }
                rules={[
                  {
                    required: isW2,
                    message: 'Social security tax is required',
                  },
                  () => ({
                    validator(_, value) {
                      if (isNaN(Number(value))) {
                        return Promise.reject(
                          new Error('Social security tax must be a number')
                        );
                      }

                      if (Number(value) < 0 || Number(value) > 100) {
                        return Promise.reject(
                          new Error(
                            'Social security tax should be between 0 and 100'
                          )
                        );
                      }

                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <Input type='number' placeholder='0' />
              </Form.Item>
            </Col>
            <Col flex={1}>
              <Form.Item
                name='medicalTax'
                label={
                  <Box display='flex' gap='1'>
                    <Text color='v2_green_7' style={{'fontSize': '18px'}}>Medicare Tax Withheld (%)</Text>
                  </Box>
                }
                rules={[
                  {
                    required: isW2,
                    message: 'Medicare tax is required',
                  },
                  () => ({
                    validator(_, value) {
                      if (isNaN(Number(value))) {
                        return Promise.reject(
                          new Error('Medicare tax must be a number')
                        );
                      }

                      if (Number(value) < 0 || Number(value) > 100) {
                        return Promise.reject(
                          new Error('Medicare tax should be between 0 and 100')
                        );
                      }

                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <Input type='number' placeholder='0' />
              </Form.Item>
            </Col>
          </Row>
          <Box
            flexDirection='column'
            bgColor='fill_1'
            bStyle='solid'
            borderRadius='4px'
            px='4'
            pb='2'
            b='line_1'
          >
            <Row align='middle'>
              <Text variant='H7' mr={0.25} py={1} color='v2_primary_bg_6'>
                Supply Share
              </Text>
              <Text color='text_2' variant='BODY_1'>
                (Salon receives $1 for every $100 the staff earns)
              </Text>
            </Row>
            <Row gutter={[16, 16]}>
              <Col xs={24} md={12}>
                <Form.Item
                  name='supplySale'
                  label={
                    <Box>
                      <Box display='flex' gap='1'>
                        Sale ($)
                      </Box>
                    </Box>
                  }
                  rules={[
                    () => ({
                      validator(_, value) {
                        if (isNaN(Number(value))) {
                          return Promise.reject(
                            new Error('Sale amount must be a number')
                          );
                        }

                        if (Number(value) < 0) {
                          return Promise.reject(
                            new Error('Sale amount must be greater or equal to 0')
                          );
                        }

                        return Promise.resolve();
                      },
                    }),
                  ]}
                >
                  <CurrencyInputPrecision height={'3rem'} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name='supplyFee'
                  label={
                    <Box display='flex' gap='1'>
                      Fee ($)
                    </Box>
                  }
                  rules={[
                    () => ({
                      validator(_, value) {
                        if (isNaN(Number(value))) {
                          return Promise.reject(
                            new Error('Fee amount must be a number')
                          );
                        }

                        if (Number(value) < 0) {
                          return Promise.reject(
                            new Error('Fee amount must be greater or equal to 0')
                          );
                        }

                        return Promise.resolve();
                      },
                    }),
                  ]}
                >
                  <CurrencyInputPrecision height={'3rem'} />
                </Form.Item>
              </Col>
            </Row>
          </Box>
        </>
      )}
    </SalaryPerTypesStyled>
  );
};

export default SalaryPerTypes;

const SalaryPerTypesStyled = styled.div`
  padding: 1rem;
  background: var(--fill-fill-1, #f6f7fc);
  box-shadow: -1px 1px 4px 0px rgba(0, 0, 0, 0.1) inset,
    0px 4px 4px 0px rgba(0, 0, 0, 0.25);
  padding: 1rem;

  .ant-form-item-label > label {
    width: 100%;
  }
`;
